// Terminal continuity service.
//
// Persisted terminal records are an internal restore queue, not visible UI.
// Running records reattach to daemon/tmux-owned PTYs. Interrupted records
// respawn a fresh PTY with transcript replay and the original cwd.
// Ordinary exited records stay inert until dismissed by retention/cleanup.

import { api } from "../api/client";
import { terminalsStore } from "../stores/terminalsStore";
import type { PersistedTerminal } from "../types";

export async function hydratePersistedTerminals(): Promise<void> {
  try {
    const records = await api.listPersistedTerminals();
    terminalsStore.setState({ persistedTerminals: records });
  } catch {
    // Persisted-terminal endpoint missing or transient; restore is best-effort.
  }
}

export async function dismissPersistedTerminalRecord(record: PersistedTerminal): Promise<void> {
  if (record.status === "running") {
    await api.deleteTerminal(record.id).catch(() => undefined);
  }
  await api.dismissPersistedTerminal(record.id).catch(() => undefined);
  terminalsStore.setState((current) => ({
    ...current,
    persistedTerminals: current.persistedTerminals.filter((item) => item.id !== record.id),
  }));
}

export async function restorePersistedTerminal(record: PersistedTerminal): Promise<boolean> {
  const alreadyLive = terminalsStore.getSnapshot().terminals.find((t) => t.id === record.id);
  if (alreadyLive) {
    terminalsStore.setState((current) => ({
      ...current,
      activeId: alreadyLive.id,
      persistedTerminals: current.persistedTerminals.filter((item) => item.id !== record.id),
    }));
    return true;
  }
  try {
    if (record.status === "running") {
      const meta = await api.spawnTerminal({
        agent: record.agent,
        sessionId: record.sessionId || undefined,
        workbenchId: record.workbenchId,
        reattachId: record.id,
        cwd: record.cwd || undefined,
        projectId: record.projectId || undefined,
        // Persisted cwd alone is enough to seed the respawn directory.
        // Sending rootPath here would force the server to resolveRoot()
        // and reject when the path is gone/forbidden — exactly the
        // continuity case where falling back to cwd matters most.
        cols: 100,
        rows: 32,
      });
      terminalsStore.setState((current) => ({
        ...current,
        terminals: current.terminals.some((t) => t.id === meta.id)
          ? current.terminals.map((t) => (t.id === meta.id ? meta : t))
          : [...current.terminals, meta],
        activeId: meta.id,
        persistedTerminals: current.persistedTerminals.filter((item) => item.id !== record.id),
      }));
      return true;
    }

    let replay = "";
    try {
      replay = await api.fetchTerminalTranscript(record.id);
    } catch {
      // No transcript; respawn without replay so the user still gets a fresh shell.
    }
    const meta = await api.spawnTerminal({
      agent: record.agent,
      sessionId: record.sessionId || undefined,
      workbenchId: record.workbenchId,
      cwd: record.cwd || undefined,
      projectId: record.projectId || undefined,
      // Persisted cwd alone is enough to seed the respawn directory.
      // Sending rootPath here would force the server to resolveRoot()
      // and reject when the path is gone/forbidden — the same continuity-
      // breaking case fixed in the running branch above.
      // The respawn gets a new id; restoreOf names the record so the host
      // refuses it, rather than falling back to its own cwd, when the cwd has
      // vanished since the startup reconcile.
      restoreOf: record.id,
      cols: 100,
      rows: 32,
    });
    // The host attaches to the record's agent instead when a supervisor still
    // holds it (the startup reconcile reaped it behind a failed probe): the
    // agent's own screen arrives on the socket, so the old transcript is not
    // painted under it, and the record, which is the live terminal's, stays.
    const reattached = meta.reattached === true;
    terminalsStore.setState((current) => ({
      ...current,
      terminals: current.terminals.some((t) => t.id === meta.id)
        ? current.terminals.map((t) => (t.id === meta.id ? meta : t))
        : [...current.terminals, meta],
      activeId: meta.id,
      persistedTerminals: current.persistedTerminals.filter((item) => item.id !== record.id),
      replayScrollback:
        replay && !reattached
          ? { ...current.replayScrollback, [meta.id]: replay }
          : current.replayScrollback,
    }));
    if (!reattached) await api.dismissPersistedTerminal(record.id).catch(() => undefined);
    return true;
  } catch (error) {
    if (isStaleRestoreRefusal(error) || isNotOwnedRestoreRefusal(error)) {
      // A stale record the host has already marked inert, as its startup
      // reconcile does for the same records; or one that is not this user's,
      // which the host will not let them dismiss either. Only drop it from the
      // queue: a dismiss is not needed, and the DELETE in
      // dismissPersistedTerminalRecord would kill any tmux/daemon session still
      // holding the id.
      terminalsStore.setState((current) => ({
        ...current,
        error: null,
        persistedTerminals: current.persistedTerminals.filter((item) => item.id !== record.id),
      }));
      return false;
    }
    if (isStalePersistedTerminalError(error)) {
      await dismissPersistedTerminalRecord(record).catch(() => undefined);
      terminalsStore.setState((current) => ({
        ...current,
        error: null,
        persistedTerminals: current.persistedTerminals.filter((item) => item.id !== record.id),
      }));
      return false;
    }
    terminalsStore.setState({
      error: error instanceof Error ? error.message : "Reattach failed",
    });
    return false;
  }
}

// The host's answer when it will not respawn a terminal whose session is gone:
// it came from another install, or its cwd is missing (src/server.js), and a
// respawn would open a shell in the host's own working directory instead.
// Read off the error payload rather than `instanceof ApiError`, so it also
// matches under tests that mock api/client.
export function isStaleRestoreRefusal(error: unknown): boolean {
  const payload = error as { data?: { code?: unknown } | null } | null | undefined;
  return payload?.data?.code === "STALE_PERSISTED_TERMINAL";
}

// The host's answer when the terminal a restore names is not the caller's:
// another user's, or theirs in another org (src/server.js). To this user it does
// not exist, so no retry can succeed.
export function isNotOwnedRestoreRefusal(error: unknown): boolean {
  const payload = error as { data?: { code?: unknown } | null } | null | undefined;
  return payload?.data?.code === "PERSISTED_TERMINAL_NOT_FOUND";
}

function isStalePersistedTerminalError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || "");
  return (
    /^Project not found: /.test(message) ||
    /^rootPath does not exist: /.test(message) ||
    /^rootPath is unreadable: /.test(message) ||
    /^rootPath must be a directory$/.test(message)
  );
}

export async function reattachAllLive(): Promise<number> {
  const live = terminalsStore
    .getSnapshot()
    .persistedTerminals.filter((record) => record.status === "running");
  if (!live.length) return 0;
  const results = await Promise.all(live.map((record) => restorePersistedTerminal(record)));
  return results.filter(Boolean).length;
}

export async function resumeInterruptedTerminals(): Promise<number> {
  const interrupted = terminalsStore
    .getSnapshot()
    .persistedTerminals.filter(
      (record) => record.status === "exited" && record.resumeEligible === true
    );
  if (!interrupted.length) return 0;
  const results = await Promise.all(interrupted.map((record) => restorePersistedTerminal(record)));
  return results.filter(Boolean).length;
}

// Module-level in-flight guard. WorkbenchView mount and the topbar
// Terminal button both call restoreContinuityTerminals concurrently.
// Without this guard, two overlapping calls each see the same
// resume-eligible exited record before the first call's setState removes
// it, and both spawn a fresh terminal. Because respawn assigns a new
// terminal id (not the persisted id), the per-record idempotency in
// restorePersistedTerminal does not catch the dup. Coalescing here is
// the cheapest fix that covers both callers.
let inflightRestore: Promise<number> | null = null;

export async function restoreContinuityTerminals(): Promise<number> {
  if (inflightRestore) return inflightRestore;
  // Hold the promise in a local so the return type stays narrowed to
  // Promise<number>. The IIFE's finally block sets the module-level
  // inflightRestore back to null on resolution, which would otherwise
  // confuse TypeScript's narrowing on the outer return.
  const promise = (async () => {
    try {
      await hydratePersistedTerminals();
      const reattached = await reattachAllLive();
      const restarted = await resumeInterruptedTerminals();
      return reattached + restarted;
    } finally {
      inflightRestore = null;
    }
  })();
  inflightRestore = promise;
  return promise;
}

export async function ensureAtLeastOneTerminal(opts: {
  workbenchId: string;
  defaultAgent: string;
  projectId?: string | null;
  rootPath?: string | null;
}): Promise<boolean> {
  const snap = terminalsStore.getSnapshot();
  if (
    snap.terminals.some(
      (terminal) => (terminal.workbenchId || "wb-default") === (opts.workbenchId || "wb-default")
    )
  )
    return false;
  if (snap.userClosedAny) return false;
  try {
    const meta = await api.spawnTerminal({
      agent: opts.defaultAgent,
      workbenchId: opts.workbenchId,
      projectId: opts.projectId || undefined,
      rootPath: opts.rootPath || undefined,
      cols: 100,
      rows: 32,
    });
    terminalsStore.setState((current) => ({
      ...current,
      terminals: [...current.terminals, meta],
      activeId: meta.id,
    }));
    return true;
  } catch (error) {
    terminalsStore.setState({
      error: error instanceof Error ? error.message : "Failed to open terminal",
    });
    return false;
  }
}
